import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useEffect, type ReactNode } from "react";
import appLogo from "../../app_logo.png";
import companyLogoMark from "../assets/company-logo-mark.png";
import { configureTokenProvider } from "./api";
import { appOrigin } from "./deployment";
import { bypassLogin, demoMode } from "./runtime";

const tenantId = import.meta.env.VITE_MICROSOFT_TENANT_ID || "common";
const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID || "00000000-0000-0000-0000-000000000000";
const apiScope = import.meta.env.VITE_API_SCOPE || `${clientId}/access_as_user`;
const sessionMaxAgeDays = Number(import.meta.env.VITE_SESSION_MAX_AGE_DAYS || "14");
const sessionStartedAtKey = "cv-session-started-at";

export const msalInstance = new PublicClientApplication({
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: appOrigin,
    postLogoutRedirectUri: appOrigin,
  },
  cache: { cacheLocation: "localStorage" },
});

function ProductionAuth({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const authenticated = useIsAuthenticated();

  useEffect(() => {
    configureTokenProvider(async () => {
      const account = instance.getActiveAccount() ?? accounts[0];
      if (!account) throw new Error("Microsoft sign-in is required");
      const result = await instance.acquireTokenSilent({ account, scopes: [apiScope] });
      return result.accessToken;
    });
  }, [accounts, instance]);

  useEffect(() => {
    if (!authenticated) return;
    const now = Date.now();
    const startedAt = Number(localStorage.getItem(sessionStartedAtKey) || now);
    if (!localStorage.getItem(sessionStartedAtKey)) localStorage.setItem(sessionStartedAtKey, String(now));
    if (Number.isFinite(startedAt) && now - startedAt >= sessionMaxAgeDays * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(sessionStartedAtKey);
      void instance.logoutRedirect({ postLogoutRedirectUri: appOrigin });
    }
  }, [authenticated, instance]);

  if (!authenticated) {
    return (
      <main className="signin-page">
        <div className="signin-atmosphere" aria-hidden="true"><span className="signin-cloud cloud-one" /><span className="signin-cloud cloud-two" /><span className="signin-orbit" /></div>
        <div className="signin-card">
          <div className="signin-title-lockup"><div className="signin-title-mark"><img src={appLogo} alt="AI CV Screening logo" /></div><h1>AI CV Screening</h1></div>
          <p className="signin-title-support">Recruitment review workspace</p>
          <p className="eyebrow">Secure company access</p>
          <h2 className="signin-action-title">Sign in to review<br />applications.</h2>
          <p className="signin-description">Use your organizational Microsoft account to review recruitment applications and evidence.</p>
          <button className="button primary signin-button" disabled={inProgress !== "none"} onClick={() => void instance.loginRedirect({ scopes: [apiScope], prompt: "select_account" })}><span className="microsoft-mark" aria-hidden="true"><i /><i /><i /><i /></span>{inProgress !== "none" ? "Connecting…" : "Sign in with Microsoft"}</button>
          <p className="signin-note">Access is managed by your company. Only approved accounts can enter this workspace.</p>
        </div>
        <aside className="signin-company-attribution company-endorsement-floating" aria-label="Company attribution"><div className="company-endorsement-mark"><img src={companyLogoMark} alt="Legend company logo" /></div><span>By Legend Interiors Limited</span></aside>
      </main>
    );
  }
  return children;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (demoMode || bypassLogin) return children;
  return <MsalProvider instance={msalInstance}><ProductionAuth>{children}</ProductionAuth></MsalProvider>;
}

export async function signOut(): Promise<void> {
  localStorage.removeItem(sessionStartedAtKey);
  if (demoMode || bypassLogin) return;
  await msalInstance.logoutRedirect({ postLogoutRedirectUri: appOrigin });
}
